import { useReducer } from 'react';

import { CallOutcome, NextCallPreset } from 'src/services/call-lists/call-lists.types';

// ----------------------------------------------------------------------

/**
 * The current-call panel's flow (JT1-1827):
 *
 *   idle ─DIAL─▶ dialling ─DIAL_OK─▶ on_call ─END_CALL / SET_OUTCOME─▶ after_call ─SAVE─▶ saving
 *                    └─DIAL_FAIL─▶ idle                                  ▲                │
 *                                                                        └──SAVE_FAIL─────┘
 *
 * Logging a call without dialling skips straight from idle to after_call when an outcome is picked.
 * A successful save resets the panel for whoever comes next.
 */
export type CallPhase = 'idle' | 'dialling' | 'on_call' | 'after_call' | 'saving';

export interface NextCallChoice {
  preset: NextCallPreset;
  date?: string;
}

export interface FollowUpTask {
  title: string;
  dueAt: string | null;
}

export interface CurrentCallState {
  phase: CallPhase;
  outcome: CallOutcome | null;
  notes: string;
  startedAt: number | null;
  endedAt: number | null;
  nextCall: NextCallChoice | null;
  followUp: FollowUpTask | null;
  error: string | null;
}

export type CurrentCallAction =
  | { type: 'DIAL' }
  | { type: 'DIAL_OK'; at: number }
  | { type: 'DIAL_FAIL' }
  | { type: 'END_CALL'; at: number }
  | { type: 'SET_OUTCOME'; outcome: CallOutcome; at: number }
  | { type: 'SET_NOTES'; notes: string }
  | { type: 'SET_NEXT_CALL'; nextCall: NextCallChoice | null }
  | { type: 'SET_FOLLOW_UP'; followUp: FollowUpTask | null }
  | { type: 'SAVE' }
  | { type: 'SAVE_FAIL'; error: string }
  | { type: 'RESET'; notes?: string };

export const initialCallState = (notes = ''): CurrentCallState => ({
  phase: 'idle',
  outcome: null,
  notes,
  startedAt: null,
  endedAt: null,
  nextCall: null,
  followUp: null,
  error: null,
});

export function currentCallReducer(state: CurrentCallState, action: CurrentCallAction): CurrentCallState {
  switch (action.type) {
    case 'DIAL':
      return state.phase === 'idle' || state.phase === 'after_call'
        ? { ...state, phase: 'dialling', error: null }
        : state;
    case 'DIAL_OK':
      return state.phase === 'dialling'
        ? { ...state, phase: 'on_call', startedAt: action.at, endedAt: null }
        : state;
    case 'DIAL_FAIL':
      return state.phase === 'dialling' ? { ...state, phase: state.outcome ? 'after_call' : 'idle' } : state;
    case 'END_CALL':
      return state.phase === 'on_call' ? { ...state, phase: 'after_call', endedAt: action.at } : state;
    case 'SET_OUTCOME':
      if (state.phase === 'saving') return state;
      return {
        ...state,
        outcome: action.outcome,
        // Picking an outcome mid-call ends it; the timer stops where the call did.
        phase: state.phase === 'dialling' ? state.phase : 'after_call',
        endedAt: state.phase === 'on_call' ? action.at : state.endedAt,
        nextCall: null,
        error: null,
      };
    case 'SET_NOTES':
      return { ...state, notes: action.notes };
    case 'SET_NEXT_CALL':
      return { ...state, nextCall: action.nextCall };
    case 'SET_FOLLOW_UP':
      return { ...state, followUp: action.followUp };
    case 'SAVE':
      return state.outcome && state.phase !== 'saving' && state.phase !== 'dialling'
        ? { ...state, phase: 'saving', endedAt: state.endedAt ?? (state.startedAt !== null ? Date.now() : null), error: null }
        : state;
    case 'SAVE_FAIL':
      return state.phase === 'saving' ? { ...state, phase: 'after_call', error: action.error } : state;
    case 'RESET':
      return initialCallState(action.notes);
    default:
      return state;
  }
}

/** Seconds on the call, or null when the call was logged without dialling. */
export const callDuration = (state: Pick<CurrentCallState, 'startedAt' | 'endedAt'>, now = Date.now()): number | null => {
  if (state.startedAt === null) return null;
  return Math.max(0, Math.round(((state.endedAt ?? now) - state.startedAt) / 1000));
};

export function useCurrentCall(initialNotes = '') {
  return useReducer(currentCallReducer, initialNotes, initialCallState);
}
